import HeaderSection from '_sections/HeaderSection';
import {HeadFC} from 'gatsby';
import * as React from 'react';
import FaqSection from '../components/FaqSection';

const aboutSlated = [
  {
    title: 'Why did we decide to build Slated?',
    content:
      'We built Slated to help intermediate lifters easily customize their workout routines and track their progress. We wanted to create an app that combines the efficiency of a digital platform with the familiar simplicity of a traditional gym log.',
  },
  {
    title: 'How can Slated help with my health & fitness?',
    content:
      'Slated helps you create custom workout routines, track your progress, and stay on schedule with your fitness goals. It provides an easy-to-use interface, customizable workouts, progress tracking, and scheduling features.',
  },
  {
    title:
      'How is Slated different from the many other fitness apps out there?',
    content: (
      <p>
        Slated focuses specifically on intermediate lifters and offers a unique
        combination of features:
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>
            Effortless customization: Design routines without repetitive set
            input.
          </li>
          <li>
            Familiar simplicity: Feels like a familiar gym log, yet intuitive
            and seamless.
          </li>
          <li>
            Progress tracking: Visualize your progress and stay motivated.
          </li>
          <li>
            Scheduling: Stay on track with reminders and avoid rainchecks.
          </li>
        </ul>
      </p>
    ),
  },
  {
    title: 'Why are there no workout programs offered on Slated?',
    content:
      'Slated believes in empowering you to create your own routines based on your specific goals and preferences. Instead of offering pre-built programs, it provides tools and features to personalize your fitness journey.',
  },
  {
    title: 'What is the best way to get started working out with Slated?',
    content:
      'Download the app, choose your desired activity (Move, Lift, Workout), and follow the instructions to set up your profile and create your first workout routine. Take advantage of the free features to explore the app and see how it works.',
  },
  {
    title: 'What is Slated’s long term goal as a fitness app?',
    content:
      'Slated aims to become the go-to personalized fitness platform for intermediate lifters. We want to empower individuals to achieve their fitness goals through a user-friendly, customizable, and data-driven approach',
  },
];

const generalTraining = [
  {
    title: 'What kind of activities does Slated support?',
    content: (
      <p>
        Slated supports three main activities:
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>
            Move: Track cardio and aerobics, including heart rate, routes, and
            progress.
          </li>
          <li>
            Lift: Plan and track strength training exercises, record weights,
            reps, sets, and use smart wearable data for analysis.
          </li>
          <li>
            Workout: Follow along with pre-designed workout routines for HIIT
            and functional training.
          </li>
        </ul>
      </p>
    ),
  },
  {
    title: `Are there other activities that are planned for the future?`,
    content: `Yes, Slated plans to support additional activities in the future to help support positive habit reinforcement. Specific details are not yet available. Stay tuned for our next update!`,
  },
  {
    title: `What kind of fitness activities suit me?`,
    content: `The best activities for you depend on your individual goals, fitness level, and preferences. Consider consulting a fitness professional for personalized advice.`,
  },
  {
    title: `What kind of fitness goals does Slated support?`,
    content: (
      <p>
        Slated supports a wide range of fitness goals, including:
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>Muscle building (hypertrophy)</li>
          <li>Strength training</li>
          <li>Weight loss</li>
          <li>Staying lean</li>
          <li>Overall fitness and health</li>
        </ul>
      </p>
    ),
  },
  {
    title: `Should I attempt to do more than 1 type of fitness activity?`,
    content: `Combining different activities can be beneficial for overall fitness. Consider incorporating cardio, strength training, and functional exercises for a well-rounded workout program. That is why our Slated Workout Planner is designed to assist you on your fitness journey.`,
  },
  {
    title: `Is it safe to perform the various activities that Slated offers?`,
    content: `Always start slowly and gradually increase intensity to avoid injuries. Consult a doctor if you have any pre-existing medical conditions or concerns.`,
  },
];

const lift = [
  {
    title: `Why is strength & weight training important for my fitness?`,
    content: `Strength training builds muscle, increases strength, improves bone density, and boosts metabolism. It provides numerous benefits for overall health and fitness.`,
  },
  {
    title: `I am new to weightlifting, will Slated work for me to get started?`,
    content: `Slated is predominantly built for intermediate lifters. However, we provide features to help beginners get started with weightlifting. That said, it's recommended to consult a certified personal trainer for proper guidance on form and technique.`,
  },
  {
    title: `What is progressive overload, why is it important for strength & weights training?`,
    content: `Progressive overload is gradually increasing the weight, reps, or sets over time to stimulate muscle growth and strength gains. It's a crucial principle for effective weight training.`,
  },
  {
    title: `How do Folders work, and how should I craft my various weightlifting workouts?`,
    content: `Folders allow you to organize your exercises into different groups based on muscle groups, workout goals, or other criteria. This helps you create focused and efficient workouts.`,
  },
  {
    title: `I workout at multiple locations where equipment varies, how do I deal with that?`,
    content: `Slated allows you to create alternative exercise variations within each exercise, ensuring you can adapt your workout to different equipment availability.`,
  },
  {
    title: `What are exercise variants?`,
    content: `Exercise variants are different ways to perform the same exercise. For example, you can do a barbell squat, a dumbbell squat, or a bodyweight squat.`,
  },
  {
    title: `There are missing exercises from the default list, what should I do?`,
    content: `If there are exercises missing from the default list, you can add them yourself. To do this, go to the "Exercises" tab and hit the "+" button, then click on the ‘+ Create Custom Exercise’ button, name your exercise and select the targeted Muscle Group from the dropdown list.`,
  },
  {
    title: `What kind of issues would cause me to re-consider if I should be weightlifting?`,
    content: (
      <p>
        There are a few different types of issues that could cause you to
        re-consider if you should be weightlifting. These include:
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>
            <b>Medical conditions:</b> If you have any medical conditions, it is
            important to talk to your doctor before starting any new exercise
            program.
          </li>
          <li>
            <b>Pain:</b> If you are experiencing pain during your workouts, it
            is important to stop and consult with a doctor or physical
            therapist.
          </li>
          <li>
            <b>Lack of motivation:</b> If you are not motivated to weightlift,
            it is unlikely that you will stick with it in the long term.
          </li>
        </ul>
      </p>
    ),
  },
];

const move = [
  {
    title: `What is a long walk, and why is it considered a fitness activity?`,
    content: `A long walk is a walk that lasts for at least 30 minutes. Walking is a great way to get your heart rate up and burn calories.`,
  },
  {
    title: `What are the benefits of running?`,
    content: `Running is a great way to improve your cardiovascular health, strengthen your muscles, and lose weight.`,
  },
  {
    title: `How long should I run for, and at what pace?`,
    content: `The ideal running distance and pace varies depending on your fitness level. However, a good starting point is to run for 30 minutes at a pace that is challenging but still allows you to hold a conversation (Zone 2).`,
  },
  {
    title: `What kind of issues would cause me to re-consider if I should be running?`,
    content: (
      <p>
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>
            <b>Medical conditions</b>: If you have any medical conditions, it is
            important to talk to your doctor before starting any new exercise
            program.
          </li>
          <li>
            <b>Pain:</b> If you are experiencing pain during your runs, it is
            important to stop and consult with a doctor or physical therapist.
          </li>
          <li>
            <b>Joint problems:</b> Running can be hard on your joints, so it is
            important to make sure you are wearing the right shoes and running
            on a soft surface.
          </li>
        </ul>
      </p>
    ),
  },
];

const workout = [
  {
    title: `Why are functional workouts important for my fitness?`,
    content: `Functional workouts are important for your fitness because they mimic the movements you do in everyday life. This can help you improve your balance, coordination, and strength.`,
  },
  {
    title: `I am new to functional workouts, will Slated work for me to get started?`,
    content: `Yes, Slated is a great way to get started with functional workouts. The app includes a variety of beginner-friendly workouts that you can follow along to.`,
  },
  {
    title: `Why should I consider performing some functional workouts instead of focusing on running?`,
    content: `Functional workouts can provide a number of benefits that running does not. For example, functional workouts can help you improve your strength, flexibility, and balance. They can also help you burn more calories.`,
  },
  {
    title: `Who is Ritual, and why are they working with Slated to provide guided workouts?`,
    content: `Ritual is a fitness company that specializes in functional workouts. They are working with Slated to provide guided workouts that users can follow along to.`,
  },
];

const others = [
  {
    title: `Are there plans to support other common main fitness activities?`,
    content: `Yes, Slated is constantly adding new features and functionality. The developers are planning to add support for additional common fitness activities in the future.`,
  },
  {
    title: `What are secondary activities, and why are they good for me?`,
    content: (
      <p>
        Secondary activities are additional exercises you can add to your main
        workout routine to target specific muscle groups or improve overall
        fitness. They can help you:
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>Improve your flexibility and range of motion</li>
          <li>Reduce your risk of injury</li>
          <li>Boost your metabolism</li>
          <li>Recover from intense workouts</li>
          <li>Add variety and fun to your workout routine</li>
        </ul>
      </p>
    ),
  },
  {
    title: `What kind of secondary activities can I expect from Slated in future?`,
    content: (
      <p>
        Slated is planning to add a variety of secondary activities, including:
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>Stretching and mobility exercises</li>
          <li>Yoga and Pilates poses</li>
          <li>Cardio exercises</li>
          <li>Core exercises</li>
          <li>Balance and stability exercises</li>
          <li>
            Relaxation and mindfulness exercises We are also intending on
            supplementing your lifting and fitness journey with other
            habit-forming activities.
          </li>
        </ul>
      </p>
    ),
  },
];

const activitiesSync = [
  {
    title: `What is the point of syncing my activities from Apple Health or Google FIT?`,
    content: (
      <p>
        You can sync your activity data from Apple Health or Google FIT to
        Slated to:
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>Get a more complete picture of your overall fitness activity</li>
          <li>Automatically track your progress over time</li>
          <li>Avoid manually entering data into multiple apps</li>
        </ul>
      </p>
    ),
  },
  {
    title: `What kinds of activities can be synced?`,
    content: (
      <p>
        The specific activities that can be synced will vary depending on the
        app you are using. However, some common activities that can be synced
        include:
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>Steps</li>
          <li>Distance</li>
          <li>Calories burned</li>
          <li>Active minutes</li>
          <li>Sleep</li>
        </ul>
      </p>
    ),
  },
  {
    title: `Aside from workout data, what other personal information is requested, and why?`,
    content: `When you sync your activities from Apple Health or Google FIT, Slated will collect some personal information, such as your name, age, and gender. This information is used to personalize your experience and provide you with more accurate fitness insights.`,
  },
];

const premium = [
  {
    title: `Is Slated free to use?`,
    content: `
    Yes, Slated is free to use for basic features. You can save and record up to three activities (Move, Lift, or Workout) without paying a subscription`,
  },
  {
    title: `What can I get access to as a free user?`,
    content: (
      <p>
        As a free user, you can:
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>Save and record up to three activities</li>
          <li>Choose from hundreds of exercises</li>
          <li>Create custom workout routines</li>
          <li>Track your progress</li>
          <li>Schedule your workouts</li>
          <li>Receive reminders</li>
          <li>Access a growing collection of workout routines</li>
        </ul>
      </p>
    ),
  },
  {
    title: `How does Slated make money as a business if it supports free users?`,
    content: `Slated generates revenue through its premium subscription, which offers additional features to help our community achieve their fitness goals better and faster.`,
  },
  {
    title: `Why should I get on Slated’s premium subscriptions?`,
    content: (
      <p>
        If you are serious about your fitness goals, then subscribing to Slated
        Premium can help you achieve them faster. You will get access to a wider
        range of features and tools that can help you:
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>Track your progress more accurately</li>
          <li>Create more effective workout routines</li>
          <li>Stay motivated and engaged</li>
          <li>Achieve your fitness goals faster</li>
        </ul>
      </p>
    ),
  },
  {
    title: `What kind of benefits do I get when I subscribe to Slated?`,
    content: (
      <p>
        Slated Premium offers a variety of benefits, including:
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>Unlimited activity tracking</li>
          <li>Access to all workout routines</li>
          <li>Advanced analytics</li>
          <li>Export data</li>
          <li>Personalized recommendations</li>
          <li>Exclusive content</li>
          <li>AI Coach and scheduling optimizer (Stay tuned!)</li>
        </ul>
      </p>
    ),
  },
  {
    title: `Is there a trial period to test out the premium benefits?`,
    content: `Yes, Slated offers a free 7-day trial of Premium for up to 3 workout sessions. This is a great way to try out all the features before you commit to a subscription.`,
  },
  {
    title: `What kind of payment plans does Slated offer?`,
    content: (
      <p>
        Slated offers two different payment plans for Premium:
        <ul className="list-disc ml-8 mt-4 flex flex-col gap-y-2">
          <li>Monthly: $13.98 per month</li>
          <li>Annual: $103.99 per year (equivalent to $8.67 per month)</li>
        </ul>
      </p>
    ),
  },
  {
    title: `How do I pause or stop my subscription to Slated if my training plans have changed?`,
    content: `You can easily pause or stop your subscription to Slated at any time through your account settings.`,
  },
];

const FAQs = () => {
  return (
    <div className="">
      <HeaderSection active="FAQ" />
      <div className="max-w-section mx-auto px-4 sm:px-44 my-20">
        <h1 className="font-bold text-3xl">FAQ</h1>
        <FaqSection title="About Slated" data={aboutSlated} />
        <FaqSection title="General Training" data={generalTraining} />
        <FaqSection title="Strength & Weights Training (Lift)" data={lift} />
        <FaqSection title="Long Walks, Jogging & Running (Move)" data={move} />
        <FaqSection
          title="Functional & HIIT Training (Workout)"
          data={workout}
        />
        <FaqSection
          title="Other Primary & Secondary Activities"
          data={others}
        />
        <FaqSection
          title="Activities Sync (Apple Health, Google FIT)"
          data={activitiesSync}
        />
        <FaqSection title="Subscription and Premium Users" data={premium} />

        <div className="mt-8 flex justify-center">
          <a
            className="text-2xl py-2 px-7 border border-gray-500 rounded-full inline-flex bg-hhhl-yellow"
            href="/">
            Back to Homepage
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQs;

const title = 'Slated FAQs';
const description = `Slated is the #1 Workout Planner App for Intermediate Lifters. Create custom workout routines, track your progress, and stay on schedule with Slated's intuitive features.`;

export const Head: HeadFC = () => (
  <>
    <title>Slated FAQs</title>
    <meta name="title" content={title} />
    <meta property="og:title" content={title} />
    <meta name="description" content={description} />
    <meta property="og:description" content={description} />
  </>
);
